import React, { useEffect, useState } from "react";
import { Button } from "../../../../../design-system";
import {
  CommunityFormatProfile,
  CommunityCustomFormat,
} from "../../../../../types/CommunityMediaConfig";
import { DiscoveredFormatSuggestion } from "../../../../../database/community/researchAudience";
import {
  researchFormatProfileForAudience,
  researchFormatElaboration,
  saveCommunityMediaConfig,
} from "../../../../../database/community/mediaConfig";
import {
  VocBlock,
  VocMeta,
  VocActionRow,
  VocChipTag,
  FormatProfileList,
  FormatProfileRow,
  FormatProfileBar,
  FormatProfileLabel,
  FormatProfileName,
  FormatProfileWeight,
  FormatProfileEvidence,
  DiscoveryCard,
  DiscoveryHeader,
  DiscoveryBadge,
  DiscoveryKey,
  DiscoveryStructure,
  DiscoveryMeta,
  SavedFormatsBlock,
  SavedFormatsTitle,
  SavedFormatRow,
  SavedFormatHeader,
  SavedFormatLabel,
  SavedFormatKey,
  SavedFormatStructure,
  SavedFormatActions,
  DiscoverySectionTitle,
  ResearchPanel,
  ResearchTitle,
  ResearchSourcesNote,
} from "./editorShared";

export type FormatProfileEditorProps = {
  communityId?: string;
  initialValue?: CommunityFormatProfile;
  existingCustomFormats?: CommunityCustomFormat[];
  hasAudienceData: boolean;
  onPersist?: (
    profile: CommunityFormatProfile,
    addedCustomFormats?: CommunityCustomFormat[]
  ) => void;
};

type FormatResearchSuggestion = {
  formatProfile: CommunityFormatProfile;
  discoveredFormats: DiscoveredFormatSuggestion[];
  rationale?: string;
  sources: string[];
};

export const FormatProfileEditor: React.FC<FormatProfileEditorProps> = ({
  communityId,
  initialValue,
  existingCustomFormats,
  hasAudienceData,
  onPersist,
}) => {
  const [profile, setProfile] = useState<CommunityFormatProfile | undefined>(
    initialValue
  );
  const [research, setResearch] = useState<FormatResearchSuggestion | null>(null);
  const [pickedDiscoveries, setPickedDiscoveries] = useState<Set<string>>(
    new Set()
  );
  const [isResearching, setIsResearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string>("");
  /** Formats whose elaboration failed — gives the UI a list to re-elaborate. */
  const [failedElaborationKeys, setFailedElaborationKeys] = useState<Set<string>>(
    new Set()
  );
  const [retryingKey, setRetryingKey] = useState<string | null>(null);

  useEffect(() => {
    setProfile(initialValue);
    setResearch(null);
    setStatus("");
  }, [communityId, initialValue]);

  const handleResearch = async () => {
    if (!communityId) {
      setStatus("Cannot research without a community.");
      return;
    }
    if (!hasAudienceData) {
      setStatus("Run audience research first — needs a persona or quotes as input.");
      return;
    }
    setIsResearching(true);
    setStatus("Looking up top-performing accounts for this audience...");
    setResearch(null);
    try {
      const result = await researchFormatProfileForAudience({
        communityId,
        apply: false,
      });
      setResearch({
        formatProfile: result.formatProfile,
        discoveredFormats: result.discoveredFormats,
        rationale: result.rationale,
        sources: result.sources,
      });
      // Pre-select all discoveries — the coach can deselect ones they don't want.
      setPickedDiscoveries(new Set(result.discoveredFormats.map((f) => f.key)));
      const newCount = result.discoveredFormats.length;
      setStatus(
        `Found ${result.formatProfile.formats.length} format pick${
          result.formatProfile.formats.length === 1 ? "" : "s"
        }${newCount > 0 ? ` · ${newCount} net-new discovered` : ""}. Review below.`
      );
    } catch (err: any) {
      console.error("Error researching formats:", err);
      setStatus(err?.message || "Format research failed. Try again.");
    } finally {
      setIsResearching(false);
    }
  };

  const handleApply = async () => {
    if (!communityId || !research) return;
    setIsSaving(true);
    try {
      const now = new Date().toISOString();
      const accepted = research.discoveredFormats.filter((d) =>
        pickedDiscoveries.has(d.key)
      );

      // Drop any profile entries that reference a discovered format the user
      // chose NOT to keep — otherwise the picker would point at a missing key.
      const droppedKeys = new Set(
        research.discoveredFormats
          .filter((d) => !pickedDiscoveries.has(d.key))
          .map((d) => d.key)
      );
      const profileFormats = research.formatProfile.formats.filter(
        (f) => !droppedKeys.has(f.format)
      );

      const existingKeys = new Set(
        (existingCustomFormats || []).map((f) => f.key)
      );
      // Only elaborate formats that aren't already in the library — if a coach
      // re-runs research and re-saves an existing format, we keep their saved
      // (potentially edited) structure rather than overwriting.
      const toElaborate = accepted.filter((a) => !existingKeys.has(a.key));

      // Run elaborations in parallel. Each call deepens the brief one-paragraph
      // structure into a full slide-by-slide creative brief tailored to this
      // audience.
      const elaborationResults: Array<{
        key: string;
        structure: string;
        failed: boolean;
      }> = [];
      if (toElaborate.length > 0) {
        setStatus(
          `Elaborating ${toElaborate.length} format${
            toElaborate.length === 1 ? "" : "s"
          }... this can take ~30s.`
        );
        let completed = 0;
        await Promise.all(
          toElaborate.map(async (d) => {
            try {
              const result = await researchFormatElaboration({
                communityId,
                format: {
                  key: d.key,
                  label: d.label,
                  icon: d.icon,
                  structure: d.structure,
                  lever: d.lever,
                  evidence: d.evidence,
                },
              });
              elaborationResults.push({
                key: d.key,
                structure: result.structure,
                failed: false,
              });
            } catch (err) {
              console.error(`Failed to elaborate format "${d.key}":`, err);
              // Fall back to the brief structure so save still succeeds.
              elaborationResults.push({
                key: d.key,
                structure: d.structure,
                failed: true,
              });
            } finally {
              completed += 1;
              setStatus(
                `Elaborating formats... ${completed}/${toElaborate.length} done.`
              );
            }
          })
        );
      }

      const failedKeys = elaborationResults
        .filter((r) => r.failed)
        .map((r) => r.key);
      setFailedElaborationKeys(new Set(failedKeys));
      const elaboratedByKey = new Map(
        elaborationResults.map((r) => [r.key, r.structure])
      );

      const additions: CommunityCustomFormat[] = toElaborate.map((d) => ({
        key: d.key,
        label: d.label,
        icon: d.icon,
        // Prefer the elaborated brief; fall back to the original.
        structure: elaboratedByKey.get(d.key) || d.structure,
        lever: d.lever,
        evidence: d.evidence,
        source: "research" as const,
        createdAt: now,
      }));
      const mergedCustomFormats = [
        ...(existingCustomFormats || []),
        ...additions,
      ];

      setStatus("Saving format profile...");
      await saveCommunityMediaConfig(communityId, {
        formatProfile: { formats: profileFormats, lastResearchAt: now },
        ...(additions.length > 0
          ? { customFormats: mergedCustomFormats }
          : {}),
      });

      const savedProfile: CommunityFormatProfile = {
        formats: profileFormats,
        lastResearchAt: now,
      };
      setProfile(savedProfile);
      onPersist?.(savedProfile, additions);
      setResearch(null);
      setPickedDiscoveries(new Set());
      const failedNote =
        failedKeys.length > 0
          ? ` · ${failedKeys.length} elaboration${failedKeys.length === 1 ? "" : "s"} failed (re-run below).`
          : "";
      setStatus(
        `Saved ${profileFormats.length} format pick${
          profileFormats.length === 1 ? "" : "s"
        }${additions.length > 0 ? ` + ${additions.length - failedKeys.length} elaborated brief${additions.length - failedKeys.length === 1 ? "" : "s"}` : ""}.${failedNote}`
      );
    } catch (err: any) {
      console.error("Error saving format profile:", err);
      setStatus(err?.message || "Save failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReElaborate = async (formatKey: string) => {
    if (!communityId) return;
    const existing = (existingCustomFormats || []).find(
      (f) => f.key === formatKey
    );
    if (!existing) {
      setStatus(`Format "${formatKey}" not found in the library.`);
      return;
    }
    setRetryingKey(formatKey);
    setStatus(`Re-elaborating "${existing.label}"... this can take ~30s.`);
    try {
      const result = await researchFormatElaboration({
        communityId,
        format: {
          key: existing.key,
          label: existing.label,
          icon: existing.icon,
          structure: existing.structure,
          lever: existing.lever,
          evidence: existing.evidence,
        },
      });
      // Update just this format's structure in mediaConfig.
      const nextCustomFormats = (existingCustomFormats || []).map((f) =>
        f.key === formatKey ? { ...f, structure: result.structure } : f
      );
      await saveCommunityMediaConfig(communityId, {
        customFormats: nextCustomFormats,
      });
      // Clear the failed flag for this key.
      setFailedElaborationKeys((prev) => {
        const next = new Set(prev);
        next.delete(formatKey);
        return next;
      });
      // Bump the parent so it picks up the new structure.
      onPersist?.(profile || { formats: [] }, [
        nextCustomFormats.find((f) => f.key === formatKey)!,
      ]);
      setStatus(`Re-elaborated "${existing.label}".`);
    } catch (err: any) {
      console.error(`Failed to re-elaborate "${formatKey}":`, err);
      setStatus(
        err?.message || `Re-elaboration failed for "${existing.label}". Try again.`
      );
    } finally {
      setRetryingKey(null);
    }
  };

  const handleClear = async () => {
    if (!communityId) return;
    if (!profile) return;
    setIsSaving(true);
    setStatus("Clearing format profile...");
    try {
      await saveCommunityMediaConfig(communityId, {
        formatProfile: { formats: [] },
      });
      setProfile(undefined);
      setStatus("Cleared.");
    } catch (err: any) {
      console.error("Error clearing format profile:", err);
      setStatus(err?.message || "Clear failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VocBlock>
      {!hasAudienceData && (
        <VocMeta>
          Run audience research first ↑ — this picker uses the saved buyer
          persona + audience voice as input to find the formats winning with
          that exact audience.
        </VocMeta>
      )}

      {profile && profile.formats.length > 0 && (
        <FormatProfileList>
          {profile.formats.map((f) => (
            <FormatProfileRow key={f.format}>
              <FormatProfileBar
                style={{ width: `${Math.max(6, Math.min(100, f.weight))}%` }}
              />
              <FormatProfileLabel>
                <FormatProfileName>{f.format}</FormatProfileName>
                <FormatProfileWeight>{Math.round(f.weight)}%</FormatProfileWeight>
              </FormatProfileLabel>
              {f.evidence && (
                <FormatProfileEvidence>{f.evidence}</FormatProfileEvidence>
              )}
            </FormatProfileRow>
          ))}
        </FormatProfileList>
      )}

      {(existingCustomFormats?.length || 0) > 0 && (
        <SavedFormatsBlock>
          <SavedFormatsTitle>Saved format briefs</SavedFormatsTitle>
          {(existingCustomFormats || []).map((cf) => {
            const failed = failedElaborationKeys.has(cf.key);
            const isShort = (cf.structure || "").length < 300;
            const needsRetry = failed || isShort;
            return (
              <SavedFormatRow key={cf.key} $failed={failed}>
                <SavedFormatHeader>
                  {cf.icon && <span>{cf.icon}</span>}
                  <SavedFormatLabel>{cf.label}</SavedFormatLabel>
                  <SavedFormatKey>{cf.key}</SavedFormatKey>
                  {failed && (
                    <VocChipTag $tag="objection">elaboration failed</VocChipTag>
                  )}
                  {!failed && isShort && (
                    <VocChipTag $tag="language">brief only</VocChipTag>
                  )}
                </SavedFormatHeader>
                <SavedFormatStructure>
                  {(cf.structure || "(no structure)")
                    .slice(0, 280)
                    .replace(/\s+/g, " ")
                    .trim()}
                  {(cf.structure || "").length > 280 ? "..." : ""}
                </SavedFormatStructure>
                <SavedFormatActions>
                  <Button
                    variant={needsRetry ? "primary" : "secondary"}
                    size="sm"
                    onClick={() => handleReElaborate(cf.key)}
                    isLoading={retryingKey === cf.key}
                    disabled={Boolean(retryingKey) || isSaving || isResearching}
                  >
                    {failed
                      ? "🔁 Retry elaboration"
                      : isShort
                      ? "🔎 Elaborate this format"
                      : "🔁 Re-elaborate"}
                  </Button>
                </SavedFormatActions>
              </SavedFormatRow>
            );
          })}
        </SavedFormatsBlock>
      )}

      <VocActionRow>
        <Button
          variant="primary"
          size="sm"
          onClick={handleResearch}
          disabled={!communityId || !hasAudienceData || isResearching || isSaving}
          isLoading={isResearching}
        >
          🔎 Find top formats
        </Button>
        {profile && profile.formats.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClear}
            disabled={isSaving || isResearching}
          >
            Clear
          </Button>
        )}
        {status && <VocMeta>{status}</VocMeta>}
        {profile?.lastResearchAt && (
          <VocMeta>
            Last researched {new Date(profile.lastResearchAt).toLocaleDateString()}
          </VocMeta>
        )}
      </VocActionRow>

      {research && (
        <ResearchPanel>
          <ResearchTitle>Suggested format mix</ResearchTitle>
          {research.rationale && <VocMeta>{research.rationale}</VocMeta>}
          <FormatProfileList>
            {research.formatProfile.formats.map((f) => {
              const isDiscovery = research.discoveredFormats.some(
                (d) => d.key === f.format
              );
              return (
                <FormatProfileRow key={f.format}>
                  <FormatProfileBar
                    style={{ width: `${Math.max(6, Math.min(100, f.weight))}%` }}
                  />
                  <FormatProfileLabel>
                    <FormatProfileName>
                      {isDiscovery && (
                        <DiscoveryBadge style={{ marginRight: 6 }}>NEW</DiscoveryBadge>
                      )}
                      {f.format}
                    </FormatProfileName>
                    <FormatProfileWeight>
                      {Math.round(f.weight)}%
                    </FormatProfileWeight>
                  </FormatProfileLabel>
                  {f.evidence && (
                    <FormatProfileEvidence>{f.evidence}</FormatProfileEvidence>
                  )}
                </FormatProfileRow>
              );
            })}
          </FormatProfileList>

          {research.discoveredFormats.length > 0 && (
            <>
              <DiscoverySectionTitle>
                <DiscoveryBadge>NEW</DiscoveryBadge>
                <span>
                  {research.discoveredFormats.length} format
                  {research.discoveredFormats.length === 1 ? "" : "s"} not in your
                  library — add to use them?
                </span>
              </DiscoverySectionTitle>
              {research.discoveredFormats.map((d) => {
                const picked = pickedDiscoveries.has(d.key);
                return (
                  <DiscoveryCard
                    key={d.key}
                    $picked={picked}
                    htmlFor={`disc-${d.key}`}
                  >
                    <DiscoveryHeader>
                      <input
                        id={`disc-${d.key}`}
                        type="checkbox"
                        checked={picked}
                        onChange={() =>
                          setPickedDiscoveries((prev) => {
                            const next = new Set(prev);
                            if (next.has(d.key)) next.delete(d.key);
                            else next.add(d.key);
                            return next;
                          })
                        }
                      />
                      {d.icon && <span>{d.icon}</span>}
                      <span>{d.label}</span>
                      <DiscoveryKey>{d.key}</DiscoveryKey>
                    </DiscoveryHeader>
                    <DiscoveryStructure>{d.structure}</DiscoveryStructure>
                    <DiscoveryMeta>
                      {d.lever && <span>Lever: {d.lever}</span>}
                      {d.evidence && <span>· {d.evidence}</span>}
                    </DiscoveryMeta>
                  </DiscoveryCard>
                );
              })}
            </>
          )}

          {research.sources.length > 0 && (
            <ResearchSourcesNote>
              Sources: {research.sources.slice(0, 4).join(" · ")}
              {research.sources.length > 4
                ? ` · +${research.sources.length - 4} more`
                : ""}
            </ResearchSourcesNote>
          )}

          <VocActionRow>
            <Button
              variant="primary"
              size="sm"
              onClick={handleApply}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save format mix
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setResearch(null)}
              disabled={isSaving}
            >
              Dismiss
            </Button>
          </VocActionRow>
        </ResearchPanel>
      )}
    </VocBlock>
  );
};

export default FormatProfileEditor;
